/* eslint-disable react/prop-types */
/* eslint-disable react-native/no-inline-styles */
/* eslint-disable react-native/no-color-literals */
import React, { useEffect, useMemo, useState } from 'react';
import { StyleSheet, View, TouchableOpacity, ScrollView } from 'react-native';
import { useSelector, useDispatch } from 'react-redux';
import { formatISO, startOfDay, endOfDay, addDays } from 'date-fns';

import SociollaText from '@components/sociolla/SociollaText';
import * as Layout from '@components/screen/Layout';
import Table from '@components/table/Table';
import TableMobile from '@components/table/TableMobile';
import Button from '@components/form/Button';
import DatePicker from '@components/form/DatePicker';
import StatusPicker from '@components/form/StatusPicker';
import StatusBadge from '@components/global/StatusBadge';
import MoreButton from '@components/navbar/MoreButton';
import NotificationSettingButton from '@components/navbar/NotificationSettingButton';
import RenderIf from '@components/global/RenderIf';
import ModalFull from '@components/modal/ModalFull';

import SalesTeamSearch from '@components/form/search/SalesTeamSearch';

import {
  hasNoAccess,
  parseDate,
  firstLetter,
  deSlug,
  exportCsvApi,
} from '@global/helpers';
import Colors from '@global/colors';
import { isWeb } from '@global/screens';
import { filterAdded, paramsReseted } from '@/store/params';
import { showPopup } from '@/store/modal';
import { showAlert } from '@/store/global';
import { MsB2b } from '@api/ms-b2b';
import { BASE_ROLES } from './constants';
import useListScreen from '@/hooks/useListScreen';
import {
  useGetRequestProductTesterListQuery,
  useGetRequestProductTesterCountQuery,
  useGetRequestProductTesterInfiniteListInfiniteQuery,
} from '@api/listApi';

// ─────────────────────────────────────────────────────────────────────────────
// Constants & pure helpers (module scope — no component state)
// ─────────────────────────────────────────────────────────────────────────────

const tableStructure = {
  customer_name: {
    head: 'Customer Name',
    body: 'customer_name',
  },
  sales_team_customer: {
    head: 'Sales Team Customer',
    body: 'sales_team_customer',
  },
  outlet_name: {
    head: 'Outlet Name',
    body: 'outlet_name',
  },
  request_product_number: {
    head: 'Request Product Number',
    body: 'request_product_number',
  },
  request_date: {
    head: 'Request Date',
    body: 'created_at',
  },
  status: {
    head: 'Status',
    body: 'status',
    type: 'slot',
  },
};

// UI filter shape → API query filter (was the inline transform in finalParam):
//  - keyword unwrapped from the search box's { $regex }
//  - status `need_approval_role_<role>` → status:need_approval + current_approval
//  - request_date range passes through
//  - single-team → forced user.team_id; otherwise from the picker → { $in }
const toApiFilter = (rawFilter = {}, singleTeam = null) => {
  const filter = {};
  const kw = rawFilter.keyword?.$regex ?? rawFilter.keyword;
  if (kw) {
    filter.keyword = kw;
  }
  if (rawFilter.status) {
    if (String(rawFilter.status).includes('need_approval_role')) {
      const role = String(rawFilter.status).split('need_approval_role_')[1];
      if (role) {
        filter.current_approval = role;
      }
      filter.status = 'need_approval';
    } else {
      filter.status = rawFilter.status;
    }
  }
  if (rawFilter.request_date) {
    filter.request_date = rawFilter.request_date;
  }
  if (singleTeam) {
    filter['user.team_id'] = { $in: [singleTeam.id] };
  } else if (rawFilter['user.team_id']?.length) {
    filter['user.team_id'] = {
      $in: rawFilter['user.team_id'].map((el) => el.id),
    };
  }
  return filter;
};

// RTK cache rows are frozen — return a new object carrying the derived display fields.
const mapRow = (item) => ({
  ...item,
  id: item._id ?? item.id,
  style: { cursor: 'pointer' },
  customer_name: item.customer?.name || '-',
  sales_team_customer: item.customer?.sales_team?.name || '-',
  outlet_name: item.outlet?.name || '-',
  request_product_number: item.reference_code || '-',
  created_at: item.created_at ? parseDate(item.created_at) : '-',
  status: item.status
    ? String(item.status).toLowerCase().replace(/ /g, '_')
    : item.status,
});

// Base statuses + dynamic "Pending Approval By <role>" derived from the matrix rule.
const fetchMatrixStatuses = async () => {
  const tempStatuses = [
    { name: 'Draft', value: 'draft' },
    { name: 'Need Approval', value: 'need_approval' },
  ];
  const matrix = await MsB2b.getMatrixApproval({
    params: {
      filter: {
        applicable_for: 'request_product_tester',
      },
    },
  });
  if (matrix?.data?.data?.length) {
    const matrixRule = matrix?.data?.data?.[0]?.rule;
    if (matrixRule) {
      const approvers = [];
      for (const rule of matrixRule) {
        const ruleApprovers = (rule?.approver || []).map((a) => a.role);
        approvers.push(...ruleApprovers);
      }
      const uniqueApprovers = [...new Set(approvers)].map((a) => ({
        name: `Pending Approval By ${firstLetter(deSlug(a))}`,
        value: `need_approval_role_${a}`,
      }));
      tempStatuses.push(...uniqueApprovers);
    }
  }
  tempStatuses.push(
    { name: 'Approved', value: 'approved' },
    { name: 'Cancelled', value: 'cancelled' },
    { name: 'Rejected', value: 'rejected' },
    { name: 'Completed', value: 'completed' },
  );
  return tempStatuses;
};

// ─────────────────────────────────────────────────────────────────────────────

function RequestProductListScreen(props) {
  // ── redux selectors ──
  const dispatch = useDispatch();
  const userData = useSelector((state) => state.auth.user);
  const showFilter = useSelector((state) => state.modal.showFilter);
  const showRequestProductExport = useSelector(
    (state) => !!state.modal.showRequestProductExport,
  );

  // ── local state ──
  const [statuses, setStatuses] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [filterExport, setFilterExport] = useState({
    from_date: '',
    to_date: '',
    status: 'all',
    sales_team: [],
  });
  const [draftFilter, setDraftFilter] = useState({});
  const [draftSalesTeam, setDraftSalesTeam] = useState('');

  const hasAccessCreate = !hasNoAccess(BASE_ROLES);
  // Single-team users are always scoped to their one team (the picker is
  // read-only for them); multi/none-team users pick freely. Plain const — it
  // feeds toApiFilter below, so it must resolve before the list hook.
  const singleTeam =
    userData?.sales_team?.length === 1 ? userData.sales_team[0] : null;

  // ── list data ──
  const { list, count, openChild, scope, params } = useListScreen({
    listHook: useGetRequestProductTesterListQuery,
    countHook: useGetRequestProductTesterCountQuery,
    infiniteHook: useGetRequestProductTesterInfiniteListInfiniteQuery,
    toFilter: (raw) => toApiFilter(raw, singleTeam),
  });

  // ── memoized values ──
  const salesTeamFilter = useMemo(() => {
    if (!userData?.sales_team?.length) {
      return {}; // no sales team assigned or undefined — show all
    }
    return {
      _id: { $in: userData.sales_team.map((team) => team.id) },
    };
  }, [userData?.sales_team]);

  const searchValue = useMemo(() => {
    const k = params.filter?.keyword;
    return k && k.$regex ? k.$regex : k ?? '';
  }, [params.filter]);

  // Web = flat array; mobile = paged infinite result.
  const rows = useMemo(() => {
    const raw = isWeb
      ? Array.isArray(list.data)
        ? list.data
        : []
      : (list.data?.pages ?? []).flat();
    return raw.map(mapRow);
  }, [list.data]);

  // ── effects ──
  useEffect(() => {
    fetchMatrixStatuses().then(setStatuses);
  }, []);

  useEffect(() => {
    try {
      const menus = [];
      if (hasAccessCreate) {
        menus.push({ type: 'function', name: 'Add', callback: handleCreate });
      }
      menus.push({
        type: 'function',
        name: 'Filter',
        callback: () => {
          dispatch(
            showPopup({ name: 'showFilter', value: 'request-product-list' }),
          );
        },
      });
      props.navigation.setOptions({
        headerRight: () => {
          return isWeb ? (
            <NotificationSettingButton {...props} />
          ) : (
            <MoreButton menu={menus} {...props} />
          );
        },
      });
    } catch (error) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.navigation, hasAccessCreate]);

  // Seed the staged filter draft from the applied filter whenever the modal opens.
  useEffect(() => {
    if (showFilter) {
      const applied = params.filter || {};
      setDraftFilter(applied);
      setDraftSalesTeam(
        applied['user.team_id'] ||
          (singleTeam ? [{ id: singleTeam.id, name: singleTeam.name }] : ''),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFilter]);

  // ── handlers ──
  const handleExportCsv = () => {
    if (userData?.sales_team?.length === 1) {
      setFilterExport({
        ...filterExport,
        sales_team: [
          {
            id: userData.sales_team[0].id,
            name: userData.sales_team[0].name,
          },
        ],
      });
    }
    dispatch(showPopup({ name: 'showRequestProductExport', value: true }));
  };

  const onExport = async () => {
    setExporting(true);
    try {
      if (!filterExport.from_date || !filterExport.to_date) {
        dispatch(
          showAlert({
            show: true,
            title: 'Error',
            description: 'Filter date must be filled',
            isConfirm: false,
          }),
        );
        setExporting(false);
        return;
      }
      const payload = {
        request_date: {
          $gte: filterExport.from_date,
          $lte: filterExport.to_date,
        },
      };
      if (filterExport.status && filterExport.status !== 'all') {
        if (filterExport.status.includes('need_approval_role')) {
          const role = filterExport.status.split('need_approval_role_')?.[1];
          if (role) {
            payload['current_approval'] = role;
          }
          payload['status'] = 'need_approval';
        } else {
          payload['status'] = filterExport.status;
        }
      }
      if (filterExport.sales_team?.length) {
        payload['customer.sales_team.id'] = {
          $in: filterExport.sales_team.map((team) => team.id),
        };
      }
      await exportCsvApi('request-products', payload, {
        isDownload: false,
        message: 'Export request will be sent to your email.',
      });
      setExporting(false);
    } catch (error) {
      dispatch(
        showAlert({
          show: true,
          title: 'Error',
          description:
            error?.response?.data?.message || 'System error occurred',
          isConfirm: false,
        }),
      );
      setExporting(false);
    }
  };

  const handleCreate = () => {
    openChild('RequestProductFormScreen');
  };

  const handleDetail = (data) => {
    openChild('RequestProductDetailScreen', {
      id: data?._id,
      reference_code: data?.reference_code,
    });
  };

  const draftDateHandler = (value, type) => {
    setDraftFilter((f) => ({
      ...f,
      request_date: {
        ...f.request_date,
        [type]: new Date(value).toISOString(),
      },
    }));
  };

  // ── render ──
  return (
    <Layout.Container>
      {isWeb && (
        <Layout.Title title="Request Product" style={{ zIndex: 9 }}>
          <View style={{ display: 'flex', flexDirection: 'row' }}>
            <Button
              type="primary"
              rounded={true}
              buttonStyle={{ width: 90 }}
              style={{ marginRight: 8 }}
              disabled={exporting}
              title={
                <SociollaText
                  type="medium"
                  style={{ fontSize: 14, color: Colors.white }}>
                  {exporting ? 'Exporting...' : 'Export CSV'}
                </SociollaText>
              }
              onPress={handleExportCsv}
            />
            {hasAccessCreate ? (
              <Button
                type="primary"
                rounded={true}
                buttonStyle={{ width: 230 }}
                title={
                  <SociollaText
                    type="medium"
                    style={{ fontSize: 14, color: Colors.white }}>
                    + Create New Request Product
                  </SociollaText>
                }
                onPress={handleCreate}
              />
            ) : null}
          </View>
        </Layout.Title>
      )}

      {isWeb ? (
        <Table
          filterName={scope}
          name="request-product-list"
          structures={tableStructure}
          loading={list.isLoading}
          refetching={list.isFetching}
          data={rows}
          showSearch={true}
          searchKey="keyword"
          searchPlaceholder="Search by customer name, request product number..."
          searchValue={searchValue}
          hasNoHide={true}
          totalData={count.data}
          skip={params.skip}
          limit={params.limit}
          onClickRow={(item) => handleDetail(item.data)}
          hasCallback={true}
          slot={{
            status: ({ data, index }) => (
              <View key={index} style={{ alignItems: 'center' }}>
                <StatusBadge status={data.status} />
                <View style={{ width: '100%' }}>
                  {data.status == 'need_approval' && data.current_approval ? (
                    <SociollaText
                      style={{
                        fontSize: 10,
                        marginTop: 5,
                        textAlign: 'center',
                      }}>
                      By: {firstLetter(deSlug(data.current_approval || ''))}
                    </SociollaText>
                  ) : (
                    false
                  )}
                </View>
              </View>
            ),
          }}
        />
      ) : (
        <TableMobile
          useLegendList
          filterName={scope}
          searchKey="keyword"
          searchValue={searchValue}
          loading={list.isLoading}
          refetching={list.isFetching}
          loadingLoadMore={list.isFetchingNextPage}
          hasNextPage={list.hasNextPage}
          onLoadMore={list.fetchNextPage}
          onRefresh={list.refetch}
          data={rows}
          totalData={count.data}
          showSearch={true}
          renderItem={({ data, index }) => (
            <TouchableOpacity
              activeOpacity={0.6}
              style={styles.item}
              key={index}
              onPress={() => handleDetail(data)}>
              <View>
                <SociollaText type="medium" style={{ fontSize: 15 }}>
                  {data.customer_name}
                </SociollaText>
                <SociollaText style={{ marginTop: 4 }}>
                  {data.request_product_number}
                </SociollaText>
                <View
                  style={{
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    marginTop: 8,
                  }}>
                  <SociollaText style={{ color: Colors.grey1, fontSize: 12 }}>
                    {data.created_at}
                  </SociollaText>
                  <View style={{ width: '30%' }}>
                    <StatusBadge status={data.status} />
                    <RenderIf
                      condition={
                        data.status == 'need_approval' && data.current_approval
                      }>
                      <SociollaText
                        style={{ textAlign: 'center', fontSize: 10 }}>
                        By: {firstLetter(deSlug(data.current_approval || ''))}
                      </SociollaText>
                    </RenderIf>
                  </View>
                </View>
              </View>
            </TouchableOpacity>
          )}
        />
      )}
      <ModalFull
        title="Export Request Product CSV"
        noTranslate={true}
        show={showRequestProductExport}
        onClose={() =>
          dispatch(
            showPopup({ name: 'showRequestProductExport', value: false }),
          )
        }>
        <View
          style={{
            height: isWeb ? 'calc(100vh - 150px)' : undefined,
          }}>
          <ScrollView contentContainerStyle={{ width: '100%' }}>
            <Layout.FormWrapper style={{ padding: 15 }}>
              <Layout.FormGroup lastItem>
                <SociollaText style={styles.filterLabel}>
                  Request Date
                </SociollaText>
                <View
                  style={{
                    width: '100%',
                    flexDirection: isWeb ? 'row' : 'column',
                    justifyContent: 'space-between',
                  }}>
                  <DatePicker
                    label="Start Date"
                    name="export_request_date_start"
                    style={{ width: isWeb ? '49%' : '100%' }}
                    value={filterExport.from_date}
                    onChange={(value) =>
                      setFilterExport({
                        ...filterExport,
                        from_date: formatISO(startOfDay(value)),
                      })
                    }
                  />
                  <DatePicker
                    label="End Date"
                    name="export_request_date_end"
                    style={{ width: isWeb ? '49%' : '100%' }}
                    value={filterExport.to_date}
                    disableBeforeDate={
                      filterExport.from_date
                        ? filterExport.from_date
                        : addDays(new Date(), 1)
                    }
                    onChange={(value) =>
                      setFilterExport({
                        ...filterExport,
                        to_date: formatISO(endOfDay(value)),
                      })
                    }
                  />
                </View>
              </Layout.FormGroup>
              <Layout.FormGroup>
                <SociollaText style={styles.filterLabel}>Status</SociollaText>
                <StatusPicker
                  label=""
                  options={[{ name: 'All', value: 'all' }, ...statuses]}
                  value={filterExport.status}
                  trackBy="value"
                  labelKey="name"
                  minWidth={80}
                  onChange={(val) =>
                    setFilterExport({ ...filterExport, status: val.value })
                  }
                />
              </Layout.FormGroup>
              <Layout.FormGroup lastItem>
                <SociollaText style={styles.filterLabel}>
                  Sales Team
                </SociollaText>
                <SalesTeamSearch
                  value={filterExport.sales_team}
                  placeholder="Select Sales Team"
                  multiple={true}
                  hideLabel
                  editable={
                    !userData?.sales_team?.length ||
                    userData.sales_team.length > 1
                  }
                  customFilter={salesTeamFilter}
                  onChange={(value) =>
                    setFilterExport({ ...filterExport, sales_team: value })
                  }
                />
              </Layout.FormGroup>
            </Layout.FormWrapper>
          </ScrollView>
          <View
            style={{
              width: '100%',
              flexDirection: 'row',
              alignItems: 'flex-end',
              justifyContent: isWeb ? 'flex-end' : 'space-between',
              paddingVertical: 20,
              paddingHorizontal: 20,
              backgroundColor: Colors.white,
            }}>
            <Button
              rounded={true}
              type="primary"
              buttonStyle={{
                width: 100,
                backgroundColor: Colors.white,
                marginRight: 10,
              }}
              title={
                <SociollaText type="medium" style={{ fontSize: 14 }}>
                  Cancel
                </SociollaText>
              }
              onPress={() =>
                dispatch(
                  showPopup({
                    name: 'showRequestProductExport',
                    value: false,
                  }),
                )
              }
            />
            <Button
              rounded={true}
              type="primary"
              buttonStyle={{ width: 100 }}
              disabled={exporting}
              title={
                <SociollaText
                  type="medium"
                  style={{ fontSize: 14, color: Colors.white }}>
                  {exporting ? 'Loading...' : 'Export'}
                </SociollaText>
              }
              onPress={() =>
                onExport().then(() =>
                  dispatch(
                    showPopup({
                      name: 'showRequestProductExport',
                      value: false,
                    }),
                  ),
                )
              }
            />
          </View>
        </View>
      </ModalFull>
      <Layout.Filter
        name="request-product-list"
        filterName={scope}
        isLocalVariable={true}
        hasFilterData={Object.keys(draftFilter).length > 0}
        onReset={() => {
          if (list.isFetching) {
            return;
          }
          setDraftFilter({});
          setDraftSalesTeam(
            singleTeam ? [{ id: singleTeam.id, name: singleTeam.name }] : '',
          );
          dispatch(paramsReseted({ key: scope }));
        }}
        onApply={() => {
          if (list.isFetching) {
            return;
          }
          dispatch(filterAdded({ key: scope, filter: draftFilter }));
          dispatch(showPopup({ name: 'showFilter', value: false }));
        }}>
        <Layout.FilterItem>
          <SociollaText style={styles.filterLabel}>Request Date</SociollaText>
          <View style={{ width: '100%', flexDirection: 'row' }}>
            <DatePicker
              label="Start Date"
              name="request_date"
              style={{
                marginRight: isWeb ? 10 : 0,
                width: isWeb ? 'auto' : '50%',
              }}
              disableAfterDate={draftFilter.request_date?.$lte}
              value={draftFilter.request_date?.$gte}
              onChange={(value) => draftDateHandler(value, '$gte')}
            />
            <DatePicker
              label="End Date"
              name="request_date"
              style={{ width: isWeb ? 'auto' : '50%' }}
              value={draftFilter.request_date?.$lte}
              disableBeforeDate={draftFilter.request_date?.$gte}
              onChange={(value) => draftDateHandler(value, '$lte')}
            />
          </View>
        </Layout.FilterItem>
        <Layout.FilterItem>
          <SociollaText style={styles.filterLabel}>Status</SociollaText>
          <StatusPicker
            label=""
            options={statuses}
            value={draftFilter['status']}
            trackBy="value"
            labelKey="name"
            minWidth={80}
            onChange={(val) =>
              setDraftFilter((f) => ({ ...f, status: val.value }))
            }
          />
        </Layout.FilterItem>
        <Layout.FilterItem lastItem>
          <SociollaText style={styles.filterLabel}>Sales Team</SociollaText>
          <SalesTeamSearch
            value={draftSalesTeam}
            placeholder="Select Sales Team"
            multiple={true}
            hideLabel
            editable={
              !userData?.sales_team?.length || userData.sales_team.length > 1
            }
            customFilter={salesTeamFilter}
            onChange={(value) => {
              setDraftSalesTeam(value);
              setDraftFilter((f) => ({ ...f, 'user.team_id': value }));
            }}
          />
        </Layout.FilterItem>
      </Layout.Filter>
    </Layout.Container>
  );
}

const styles = StyleSheet.create({
  item: {
    width: '100%',
    paddingVertical: 15,
    paddingHorizontal: 15,
    backgroundColor: Colors.white,
    borderBottomWidth: 1,
    borderBottomColor: Colors.grey0,
  },
  filterLabel: {
    fontSize: 13,
    marginBottom: 10,
  },
});

export default RequestProductListScreen;
